import {createFocusTrap} from 'focus-trap';
/**
 * Options object can accept:
 * @param dialogEl - Modal dialog node
 * @param modalId - required to be able to close
 * @param [options] - triggerEl, toggleEl, focusEl, timerReveal
 */
export default class ModalDialog {
  constructor(dialogEl, options, callback) {
    if (!dialogEl) return;

    this.options = typeof options === 'object' ? options : null;
    const {modalId, triggerEl, toggleEl, focusEl, timerReveal} = this.options;
    const nextPopup = localStorage.getItem(`timerPopup-${modalId}`);

    if (!modalId) throw new Error('Missing modalId');

    const modalCloseBtns = dialogEl.querySelectorAll(`[data-modal-close="modal-${modalId}"]`);

    this.modalIsOpen = false;

    const focusTrap = createFocusTrap(dialogEl, {
      initialFocus: focusEl,
      allowOutsideClick: true,
      clickOutsideDeactivates: true,
    });

    const showModal = (trigger) => {
      dialogEl.setAttribute('aria-modal', true);
      dialogEl.removeAttribute('aria-hidden');
      dialogEl.classList.add('modal--visible');
      document.documentElement.classList.add('modal-js--open');
      document.documentElement.classList.add(`${modalId}-modal-js--open`);
      const focusTrapDelay = 500;

      if (modalId === 'login' && document.documentElement.classList.contains('modal-search-open')) {
        document.dispatchEvent(new Event('toggleSearch'));
      }

      setTimeout(() => {
        focusTrap.activate();
      }, focusTrapDelay);
      this.modalIsOpen = true;

      toggleEl?.forEach((button) => button.classList.add('is-active'));

      triggerEl?.forEach((button) => button.classList.add('is-active'));

      if (callback) callback(trigger, dialogEl);
    };

    const closeModal = () => {
      dialogEl.removeAttribute('aria-modal');
      dialogEl.setAttribute('aria-hidden', true);
      dialogEl.classList.remove('modal--visible');
      focusTrap.deactivate();
      this.modalIsOpen = false;

      toggleEl?.forEach((button) => button.classList.remove('is-active'));

      triggerEl?.forEach((button) => button.classList.remove('is-active'));

      document.documentElement.classList.remove(`${modalId}-modal-js--open`);
      if (!document.querySelector('.modal--visible')) {
        document.documentElement.classList.remove('modal-js--open');
      }

      window.dispatchEvent(new CustomEvent(`${modalId}_modal_closed`, {}));
    };

    window.addEventListener('closeModal', () => {
      closeModal();
    });

    window.addEventListener(`close_modal:${modalId}`, () => {
      closeModal();
    });

    window.addEventListener(`open_modal:${modalId}`, () => {
      showModal();
    });

    const toggleModal = (event) => {
      if (this.modalIsOpen) {
        closeModal();
      } else {
        // Check if header is positioned at the bottom and scroll to it when login modal toggle is clicked
        const isLoginToggle = event.currentTarget.hasAttribute('data-login-modal-trigger');
        if (isLoginToggle && document.body.classList.contains('has-header-bottom')) {
          const headerSection = document.querySelector('.header-section');
          if (headerSection.offsetTop > 0) {
            window.scrollTo({
              top: headerSection.offsetTop + 1,
            });

            setTimeout(() => {
              showModal();
            }, 400);

            return;
          }
        }

        showModal();
      }
    };

    dialogEl.addEventListener('click', (evt) => {
      if (evt.target.closest('.modal__dialog')) return;
      closeModal();
    });

    document.addEventListener(
      'click',
      (evt) => {
        if (evt.target.closest('.modal__dialog') || !this.modalIsOpen) return;

        const isToggleElem = toggleEl ? [...toggleEl].some((elem) => elem === evt.target) : false;
        if (isToggleElem) return;

        closeModal();
      },
      {capture: true}
    );

    window.addEventListener('closeModal', () => {
      closeModal();
    });

    toggleEl?.forEach((button) => {
      button.addEventListener('click', (evt) => {
        evt.preventDefault();
        toggleModal(evt);
      });
    });

    triggerEl?.forEach((button) => {
      button.addEventListener('click', (evt) => {
        evt.preventDefault();
        showModal(button);
      });
    });

    if (timerReveal && window.localStorage) {
      let now = new Date();
      now = now.setHours(now.getHours());
      if (!nextPopup || nextPopup <= now) {
        window.setTimeout(() => {
          showModal();
        }, timerReveal);
      }
    }

    modalCloseBtns?.forEach((button) => {
      button.addEventListener('click', (evt) => {
        evt.preventDefault();
        closeModal();

        if (timerReveal && window.localStorage && modalId) {
          if (nextPopup > new Date()) {
            return;
          }

          let expires = new Date();
          expires = expires.setHours(expires.getHours() + 24);
          localStorage.setItem(`timerPopup-${modalId}`, expires);
        }
      });
    });
  }
}
